'use strict';

/**
 * AnswerStore — plain in-memory store for answer state.
 *
 * Data container only: no DOM access, no jQuery, no `page_state`.
 * Holds answers keyed by variable name and notifies subscribers when
 * a value is set. Exposed globally as `Gryphon.answers` (mirroring the
 * `Gryphon.util` pattern) so legacy code and QSL can reach it without
 * imports.
 */

var answers = {};
var subscribers = [];

/**
 * Notify all current subscribers of a single answer change.
 * Iterates over a copy so subscribers may unsubscribe during
 * notification without skipping others.
 * @param {string} name Variable name.
 * @param {*} value New value.
 */
function notify(name, value) {
  var subs = subscribers.slice();
  for (var i = 0; i < subs.length; i++) {
    try {
      subs[i](name, value);
    } catch (e) {
      // Keep the store dependency-free: console only.
      // TODO route into the mismatch/error telemetry channel.
      console.error('AnswerStore subscriber error:', e);
    }
  }
}

var store = {
  /**
   * Get the value for one variable.
   * @param {string} name Variable name.
   * @returns {*} The stored value, or `undefined` if not set.
   */
  get: function (name) {
    return answers.hasOwnProperty(name) ? answers[name] : undefined;
  },

  /**
   * Get a shallow copy of all answers. Mutating the returned object
   * does not affect the store.
   * @returns {Object} Copy of all answers.
   */
  getAll: function () {
    var copy = {};
    for (var name in answers) {
      if (answers.hasOwnProperty(name)) {
        copy[name] = answers[name];
      }
    }
    return copy;
  },

  /**
   * Set one answer and notify subscribers with `(name, value)`.
   * @param {string} name Variable name.
   * @param {*} value Value to store.
   */
  set: function (name, value) {
    answers[name] = value;
    notify(name, value);
  },

  /**
   * Bulk set. Notifies subscribers once per key, like `set`.
   * @param {Object} obj Map of variable names to values.
   */
  merge: function (obj) {
    for (var name in obj) {
      if (obj.hasOwnProperty(name)) {
        store.set(name, obj[name]);
      }
    }
  },

  /**
   * Wipe all answers and re-seed from `initial` (if given).
   * Does NOT fire per-key notifications.
   * @param {Object} [initial] Map of variable names to values.
   */
  reset: function (initial) {
    answers = {};
    for (var name in initial) {
      if (initial.hasOwnProperty(name)) {
        answers[name] = initial[name];
      }
    }
  },

  /**
   * Subscribe to answer changes.
   * @param {function(string, *)} fn Called with `(name, value)` on `set`.
   * @returns {function} Unsubscribe function.
   */
  subscribe: function (fn) {
    subscribers.push(fn);
    return function unsubscribe() {
      var index = subscribers.indexOf(fn);
      if (index !== -1) {
        subscribers.splice(index, 1);
      }
    };
  },
};

global.Gryphon = global.Gryphon || {};
Gryphon.answers = store;

export default store;
